import gsap from 'gsap'
import {
  ArcCurve,
  CatmullRomCurve3,
  Color,
  DoubleSide,
  Euler,
  Group,
  Mesh,
  PlaneGeometry,
  Quaternion,
  ShaderMaterial,
  TextureLoader,
  TubeGeometry,
  Vector2,
  Vector3,
  Vector4,
} from 'three'
import {GLTFLoader} from 'three/examples/jsm/loaders/GLTFLoader.js'

import copilotGlb from '../../../../assets/images/brandedDivider/copilot.glb'
import copilotNormalEye from '../../../../assets/images/brandedDivider/copilot-normal-eye.png'
import copilotSmileEye from '../../../../assets/images/brandedDivider/copilot-smile-eye.png'
import type {BrandedDividerParams} from '../../../../components/WebGLContext/WebGLContext.types'
import fragmentShader from '../../../_shaders/brandedDivider/copilot'
import copilotEyeFragmentShader from '../../../_shaders/brandedDivider/copilotEye'
import tubeFragmentShader from '../../../_shaders/brandedDivider/copilotTube'
import tubeVertexShader from '../../../_shaders/brandedDivider/copilotTubeVert'
import vertexShader from '../../../_shaders/vertexShader'
export default class BaseConpilot {
  group: Group = new Group()
  group2: Group = new Group()
  group3: Group = new Group()
  params: BrandedDividerParams
  curve: ArcCurve | CatmullRomCurve3 = new ArcCurve(0, 0, 5, 0, Math.PI * 1.5, false) // default value, will be overwritten in createPath
  progress: Vector4 = new Vector4(0, 0, 0, 0)
  tube: Mesh | null = null
  offsetPos: Vector2 = new Vector2(0, 0)
  rectData: Vector4 = new Vector4(0, 0, 0, 0)
  tl: gsap.core.Timeline = gsap.timeline({paused: true})
  isLoaded: boolean = false
  isNeededToPlayTl: boolean = false
  tmpEuler: Euler = new Euler()
  tmpQuaternion: Quaternion = new Quaternion()
  constructor(params: BrandedDividerParams, rectData: Vector4) {
    this.params = params
    this.rectData = rectData
    this.group.add(this.group2)
    this.group2.add(this.group3)
  }

  async create(group: Group, tl: gsap.core.Timeline) {
    const textureLoader = new TextureLoader()

    const eyeSrc = this.params.layout === 0 ? copilotSmileEye : copilotNormalEye

    const eyeTexture = await textureLoader.loadAsync(eyeSrc)
    eyeTexture.anisotropy = 8
    const eyeTextureAspect = eyeTexture.image.height / eyeTexture.image.width
    const glslLoader = new GLTFLoader()
    glslLoader.load(copilotGlb, glsl => {
      // Load copilotSmileEye texture asynchronously
      glsl.scene.traverse(child => {
        if (child instanceof Mesh) {
          const mesh = child.clone()
          const uniforms = {
            uColor1: {
              value: new Color(0x5162ff), // blue
            },
            uColor2: {
              value: new Color(0x93e3ff), // light blue
            },
            uColor3: {
              value: new Color(0xe4d1ff), // pink
            },
            uRectData: {
              value: this.rectData,
            },

            uNoiseScale: {
              value: 0.5,
            },
          }

          const defines = {
            PART: 0,
          }

          mesh.material = new ShaderMaterial({
            vertexShader,
            fragmentShader,
            uniforms,
            side: DoubleSide,
            defines,
          })

          switch (child.name) {
            case 'Goggle':
            case 'Ears':
            case 'Glass':
              const copyMesh = new Mesh(mesh.geometry.clone(), mesh.material)
              copyMesh.position.copy(mesh.position)
              copyMesh.geometry.scale(-1, 1, 1)
              copyMesh.position.x *= -1
              this.group3.add(copyMesh)
              this.group3.add(mesh)

              break
            case 'Eyes':
              const scale = this.params.layout === 0 ? 0.7 : 0.6

              const eyePlaneMesh = new Mesh(
                new PlaneGeometry(scale, scale * eyeTextureAspect),
                new ShaderMaterial({
                  vertexShader,
                  fragmentShader: copilotEyeFragmentShader,
                  uniforms: {
                    uTexture: {value: eyeTexture},
                    uRectData: {value: this.rectData},
                  },
                  transparent: true,
                  side: DoubleSide,
                }),
              )

              eyePlaneMesh.geometry.rotateY(-0.3)

              if (this.params.layout === 0) {
                eyePlaneMesh.position.set(-0.45, -0.1, 1.45)
              } else {
                eyePlaneMesh.position.set(-0.4, -0.1, 1.5)
              }

              const eyePlaneMesh2 = eyePlaneMesh.clone()
              eyePlaneMesh2.scale.set(-1, 1, 1)
              eyePlaneMesh2.position.set(-eyePlaneMesh.position.x, eyePlaneMesh.position.y, eyePlaneMesh.position.z)

              this.group3.add(eyePlaneMesh)
              this.group3.add(eyePlaneMesh2)
              break

            default:
              this.group3.add(mesh)
              break
          }

          switch (child.name) {
            case 'Goggle':
              uniforms.uColor1.value.set(0x184bf8)
              uniforms.uColor2.value.set(0x93e3ff)
              uniforms.uNoiseScale.value = 0.6
              defines.PART = 1
              break
            case 'Glass':
            case 'Screen':
              uniforms.uColor1.value.set(0x0525f1)
              uniforms.uColor2.value.set(0x01127b)
              uniforms.uNoiseScale.value = 0.5
              defines.PART = 2
              break
            case 'Ears':
              uniforms.uNoiseScale.value = 0.2
              break
          }
        }
      })

      group.add(this.group)

      this.createTl()
      this.isLoaded = true
    })

    this.createPath()
  }

  createTl() {
    if (this.isNeededToPlayTl) {
      this.tl.play()
    }
  }

  createPath() {}

  createTube() {
    const points3d = this.curve.getPoints(50).map(point => new Vector3(point.x, point.y, 0))

    const curve3D = new CatmullRomCurve3(points3d)

    // Create tube mesh
    const tubeGeometry = new TubeGeometry(curve3D, 64, 0.35, 8, false)
    const tubeMaterial = new ShaderMaterial({
      vertexShader: tubeVertexShader,
      fragmentShader: tubeFragmentShader,
      transparent: true,
      uniforms: {
        uColor1: {value: new Color(0x3194ff)}, // light blue
        uColor2: {value: new Color(0x9fecff)}, // lighter blue
        uProgress: {value: this.progress},
        uRectData: {
          value: this.rectData,
        },
      },
    })
    this.tube = new Mesh(tubeGeometry, tubeMaterial)
    this.group.add(this.tube)

    this.tube.position.set(this.offsetPos.x, this.offsetPos.y, 0)
  }

  playTl() {
    this.isNeededToPlayTl = true
    if (this.isLoaded) {
      this.tl.play()
    }
  }

  moveOnArc() {}

  update(scrollProgress: number) {
    this.moveOnArc()
    this.tmpEuler.set(-scrollProgress * 0.6, 0, 0)
    this.group.quaternion.setFromEuler(this.tmpEuler)
  }
}
